import React, { useEffect, useRef, useState } from 'react';
import FeedAd from './FeedAd';

interface AdSlotProps {
  placement: 'feed' | 'sidebar' | 'home_feed' | 'midroll_pulse' | string;
  variant?: 'overlay' | 'card' | 'inline';
  autoCloseSeconds?: number;
  onComplete?: () => void;
  /** Content the ad is shown alongside; its creator earns a revenue share. */
  contentType?: 'post' | 'pulse';
  contentId?: string;
}

const AdSlot: React.FC<AdSlotProps> = ({ placement, variant, autoCloseSeconds = 5, onComplete, contentType, contentId }) => {
  const normalizedPlacement = placement === 'sidebar' ? 'sidebar' : placement === 'midroll_pulse' ? 'video_midroll' : 'feed';
  const [remaining, setRemaining] = useState(autoCloseSeconds);
  const [ready, setReady] = useState(false);
  const markReady = useRef(() => setReady(true)).current;
  const doneRef = useRef(false);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;
  const finish = useRef(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    completeRef.current?.();
  }).current;

  useEffect(() => {
    if (variant !== 'overlay' || !ready) return undefined;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [variant, ready]);

  useEffect(() => {
    if (variant === 'overlay' && remaining <= 0) finish();
  }, [variant, remaining, finish]);

  if (variant !== 'overlay') {
    return <FeedAd placement={normalizedPlacement} contentType={contentType} contentId={contentId} />;
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.75)', visibility: ready ? 'visible' : 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 420, position: 'relative' }}>
        <button
          type="button"
          onClick={finish}
          style={{ position: 'absolute', top: -40, right: 0, background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: 16, padding: '6px 12px', cursor: 'pointer' }}
        >
          {remaining > 0 ? `Continue in ${remaining}s` : 'Continue'} ?
        </button>
        <FeedAd placement={normalizedPlacement} contentType={contentType} contentId={contentId} onNoAd={finish} onReady={markReady} />
      </div>
    </div>
  );
};

export default AdSlot;
